import { useCurrentAccount, useGetBreeds as useGetBreedOptions } from '@application/api';
import { SeoController, SubscribeToDogBreedButtonController } from '@application/components';
import { CACHE_KEY_COMPARATOR_EMAIL } from '@application/constants';
import { useNewsletter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { EmailContactCreatedFromEnum } from '@gql/graphql';
import { BreedComparatorPage } from '@presentation/pages';
import { useTranslation } from '@presentation/utils';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useToggleDogBreedInDogBreedInterestSubscription } from '../../SubscribeToDogBreedButtonController/api/toggleDogBreedInDogBreedInterestSubscription';
import { useFormatBreedsForComparison } from './api/useFormatBreedsForComparison';
import { useFormatCategories } from './api/useFormatCategories';
import { useGetBreeds } from './api/useGetBreeds';

export const BreedComparatorPageController: FC = () => {
  const { translate } = useTranslation();
  const { currentAccountLoggedIn, currentAccount } = useCurrentAccount();
  const { breedOptionsBasenameKey } = useGetBreedOptions();
  const [newsletterFormSubmitted, setNewsletterFormSubmitted] = useState(false);
  const { toggleDogBreedInDogBreedInterestSubscriptionFromButton } =
    useToggleDogBreedInDogBreedInterestSubscription();

  const cachedEmail = useMemo(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(CACHE_KEY_COMPARATOR_EMAIL);
    }
  }, []);

  const form = useFormik({
    initialValues: {
      selectedBreeds: [],
    },
    onSubmit: () => {},
  });

  const { newsletterProps, email, hasADog } = useNewsletter({
    shouldShowToast: false,
    shouldTriggerGtm: false,
    onSubmit: (values) => {
      setNewsletterFormSubmitted(true);
      Analytics.sendGenerateLeadEvent('comparator');
      localStorage.setItem(CACHE_KEY_COMPARATOR_EMAIL, values.email);
    },
    createdFrom: EmailContactCreatedFromEnum.BREED_COMPARATOR,
  });

  const showNewsletterForm =
    !currentAccountLoggedIn &&
    !cachedEmail &&
    !newsletterFormSubmitted &&
    form.values.selectedBreeds.length === 2;

  const { breeds } = useGetBreeds(form.values.selectedBreeds);
  const { formattedBreeds } = useFormatBreedsForComparison(breeds);
  const { formattedCategories } = useFormatCategories(breeds);

  return (
    <>
      <SeoController
        title={translate('breedComparator.seo.title')}
        description={translate('breedComparator.seo.description')}
        noIndex
      />
      <FormikProvider value={form}>
        <BreedComparatorPage
          breedOptions={breedOptionsBasenameKey}
          formattedBreeds={formattedBreeds}
          formattedCategories={formattedCategories}
          showNewsletterForm={showNewsletterForm}
          newsletterForm={newsletterProps}
          renderSubscribeBtn={(breedName: string) => {
            return (
              <SubscribeToDogBreedButtonController
                subscriptionEmail={(currentAccount?.email || email || cachedEmail) ?? null}
                breedName={breedName}
                openTooltipOnClick={false}
                hasADog={hasADog}
              />
            );
          }}
        />
      </FormikProvider>
    </>
  );
};
